import { useCallback, useContext } from 'react';

import { logError } from '@/providers/ErrorTracking';

import AuthContext from './AuthContext';
import clearJWTCookie from './clearJWTCookie';
import { type AuthUser, type ExposedUser } from './constants';
import createJWTCookie from './createJWTCookie';
import decodeToken from './decodeToken';
import validUser from './validUser';

type SetUser = (newUser: AuthUser | null) => void;
type SetUserByToken = (token: string | null) => AuthUser | null;

const useUser = (): [ExposedUser, SetUser, SetUserByToken] => {
  const { user, setUser: setUserInContext } = useContext(AuthContext);

  const setUser: SetUser = useCallback(
    (newUser) => {
      if (newUser) {
        setUserInContext(newUser);
      } else {
        setUserInContext(null);
      }
    },
    [setUserInContext],
  );

  const setUserByToken: SetUserByToken = useCallback(
    (token) => {
      clearJWTCookie();
      if (token) {
        try {
          const newUser = decodeToken(token);
          createJWTCookie(token);
          setUserInContext(newUser);
          return newUser;
        } catch (error) {
          logError(error);
        }
      }
      setUserInContext(null);
      return null;
    },
    [setUserInContext],
  );

  return [validUser(user), setUser, setUserByToken];
};

export default useUser;
